import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {ReactComponent as FavoriteIcon} from 'assets/svg/favorite.svg';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/registries/gtinsGrid/table';
import {GtinsGridFormattedRow, GtinsGridRawRow} from 'types/gtins';

import {Controls} from '../Controls';
import {GridsNavigation} from '../GridsNavigation';
import {FavoriteButton, FavoriteCell} from '../styled';

import {ControlsWrapper} from './styled';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (data: GtinsGridRawRow[] | undefined, t: TFunction) => GtinsGridFormattedRow[];
    headerControls?: ReactNode;
    onSelectedGtinsChange?: (gtins: string[]) => void;
    resetSelectedRows?: boolean;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const GtinsGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    onSelectedGtinsChange,
    resetSelectedRows,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const location = useLocation();
    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    const [selectedRows, setSelectedRows] = React.useState<GtinsGridFormattedRow[]>([]);
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);
    const isBulkFavoritesRoute = [
        ROUTE.REGISTRIES_GTIN_MY_CREATED,
        ROUTE.REGISTRIES_GTIN_ALL,
        ROUTE.REGISTRIES_GTIN_FAVORITES,
    ].includes(location.pathname as ROUTE);
    const isFavoritesRoute = location.pathname === ROUTE.REGISTRIES_GTIN_FAVORITES;
    const selectedGtins = React.useMemo(
        () => selectedRows.map((row) => row.rawGtin).filter(Boolean) as string[],
        [selectedRows],
    );
    React.useEffect(() => {
        onSelectedGtinsChange?.(selectedGtins);
    }, [onSelectedGtinsChange, selectedGtins]);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const defaultHeaderControls = (
        <ControlsWrapper>
            <Controls />
            <GridsNavigation />
        </ControlsWrapper>
    );
    return (
        <>
            <Gate />
            <Table
                isControlsOnTop={true}
                columns={columns}
                data={mappedData}
                hasNext={data?.hasNext}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                selectable={isBulkFavoritesRoute}
                isRowSelectable={(row: GtinsGridFormattedRow) =>
                    isFavoritesRoute || !row.rawIsFavorite
                }
                renderUnselectableRowSelection={() => (
                    <FavoriteCell>
                        <FavoriteButton type="button" isActive disabled>
                            <FavoriteIcon />
                        </FavoriteButton>
                    </FavoriteCell>
                )}
                getRowId={(row: GtinsGridFormattedRow) => String(row.rawGtin ?? row.productId)}
                onRowSelect={setSelectedRows}
                untoggleAllCheckedRows={resetSelectedRows}
                exportConfig={exportConfig}
                headerControls={headerControls ?? defaultHeaderControls}
                userConfig={{
                    gridName: 'gtinsGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
            />
        </>
    );
};
